/**
 * 设备管理 API 服务
 */
import apiClient from './api'
import type {
  Device,
  RoomDevicesDetail,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  UpdateDeviceLayoutRequest,
  FloorplanUploadResponse,
  BatchUpdateDevicesRequest,
} from '@/types/devices'

const DEVICES_BASE_URL = '/devices'

export const devicesApi = {
  /**
   * 获取房间设备列表
   */
  getRoomDevices: async (roomId: number) => {
    const response = await apiClient.get<RoomDevicesDetail>(
      `${DEVICES_BASE_URL}/rooms/${roomId}`
    )
    return response.data
  },

  /**
   * 获取房间设备详细信息（包含平面图）
   */
  getRoomDevicesDetail: async (roomId: number) => {
    const response = await apiClient.get<RoomDevicesDetail>(
      `${DEVICES_BASE_URL}/rooms/${roomId}/detailed`
    )
    return response.data
  },

  /**
   * 获取场馆可分配设备列表
   */
  getVenueAvailableDevices: async (venueId: number) => {
    const response = await apiClient.get<{
      devices: Device[]
      total: number
      online: number
      offline: number
    }>(`${DEVICES_BASE_URL}/venues/${venueId}/available-devices`)
    return response.data
  },

  /**
   * 获取所有设备列表（支持过滤）
   * @param merchantId 商家ID
   * @param venueId 场馆ID（可选）
   * @param roomId 房间ID（可选）
   * @param deviceType 设备类型（可选）
   * @param status 状态筛选（可选）
   */
  getAllDevices: async (params: {
    merchantId?: number
    venueId?: number
    roomId?: number
    deviceType?: string
    status?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params.merchantId) queryParams.append('merchant_id', String(params.merchantId))
    if (params.venueId) queryParams.append('venue_id', String(params.venueId))
    if (params.roomId) queryParams.append('room_id', String(params.roomId))
    if (params.deviceType) queryParams.append('device_type', params.deviceType)
    if (params.status) queryParams.append('status', params.status)

    const response = await apiClient.get<{
      devices: Device[]
      total: number
    }>(`${DEVICES_BASE_URL}?${queryParams.toString()}`)
    return response.data
  },

  /**
   * 创建设备
   */
  createDevice: async (roomId: number, data: CreateDeviceRequest) => {
    const response = await apiClient.post<Device>(
      `${DEVICES_BASE_URL}`,
      data
    )
    return response.data
  },

  /**
   * 更新设备
   */
  updateDevice: async (deviceId: number, data: UpdateDeviceRequest) => {
    const response = await apiClient.put<Device>(
      `${DEVICES_BASE_URL}/${deviceId}`,
      data
    )
    return response.data
  },

  /**
   * 批量更新设备（用于移除到未分配/批量添加到房间）
   */
  batchUpdateDevices: async (data: BatchUpdateDevicesRequest) => {
    const response = await apiClient.put<{
      updated: number
      failed: number
      errors: Array<{ device_id: number; error: string }>
    }>(`${DEVICES_BASE_URL}/batch-update`, data)
    return response.data
  },

  /**
   * 从房间移除设备（设置 room_id 为 null）
   */
  removeDeviceFromRoom: async (deviceId: number) => {
    return devicesApi.updateDevice(deviceId, { room_id: 0 })
  },

  /**
   * 删除设备
   */
  deleteDevice: async (deviceId: number) => {
    const response = await apiClient.delete<{ success: boolean }>(
      `${DEVICES_BASE_URL}/${deviceId}`
    )
    return response.data
  },

  /**
   * 批量导入设备
   */
  batchImportDevices: async (roomId: number, devices: CreateDeviceRequest[]) => {
    const response = await apiClient.post<{ success_count: number; failed_count: number }>(
      `${DEVICES_BASE_URL}/batch-import`,
      { room_id: roomId, devices }
    )
    return response.data
  },

  /**
   * 上传平面图
   */
  uploadFloorplan: async (
    roomId: number,
    file: File,
    gridWidth: number = 10,
    gridHeight: number = 10
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('grid_width', String(gridWidth))
    formData.append('grid_height', String(gridHeight))

    const response = await apiClient.post<FloorplanUploadResponse>(
      `${DEVICES_BASE_URL}/rooms/${roomId}/floorplan`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  /**
   * 更新设备布局位置
   */
  updateDeviceLayout: async (
    roomId: number,
    deviceId: number,
    position: UpdateDeviceLayoutRequest
  ) => {
    const response = await apiClient.put<Device>(
      `${DEVICES_BASE_URL}/rooms/${roomId}/devices/${deviceId}/layout`,
      position
    )
    return response.data
  },
}
